import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  FormHelperText,
  MenuItem,
  Select,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Locale } from "../../utils/Locales";
import { dropdownMenuProps, textFieldSx } from '../../utils/dropdownStyles';
import ToolIcon from "../../utils/ToolIcon";
import { useLocalization } from "../../context/LocalizationContext";
import MediaPreviewOverlay from "../common/MediaPreviewOverlay";
import useUrlValidation from "../../hooks/useUrlValidation";
import UrlStatusIcon from "../common/UrlStatusIcon";
import { describeMediaUrlStatus } from "../../utils/validators";

const MEDIA_KINDS = new Set(['image', 'video', 'audio']);

// Extracted into its own component so useUrlValidation can be called
// at component level (hooks cannot be called inside a .map() callback).
const UrlTextFieldRow = ({
  pair, index, attribute, label,
  urlValidationType, previewable,
  onTextChange, onLangChange, onRemove, onPreview,
  availableLocales, canRemove, onStatusChange,
  disabled = false,
  required = false,
  groupHasValue = true,
  maxLength,
}) => {
  const { status: urlStatus, reason: urlReason } = useUrlValidation(pair[attribute], urlValidationType);

  useEffect(() => {
    if (onStatusChange) onStatusChange(index, urlStatus);
  }, [index, urlStatus, onStatusChange]);

  const tooLong = Boolean(maxLength) && String(pair[attribute] ?? '').trim().length > maxLength;

  const helperText = disabled ? ''
    : tooLong ? `At most ${maxLength} characters`
    : (required && !groupHasValue) ? 'Required'
    : MEDIA_KINDS.has(urlValidationType)
      ? describeMediaUrlStatus(pair[attribute], urlStatus, urlReason, urlValidationType)
      : urlStatus === 'invalid' ? 'URL is not reachable or has an invalid format'
      : urlStatus === 'checking' ? 'Checking…'
      : urlStatus === 'valid' ? 'Valid'
      : '';

  return (
    <Box display="flex" alignItems="center" mb={1}>
      <TextField
        fullWidth
        placeholder={label}
        label={label}
        value={pair[attribute]}
        onChange={(e) => onTextChange(index, e.target.value)}
        error={!disabled && (urlStatus === 'invalid' || tooLong)}
        helperText={helperText}
        disabled={disabled}
        sx={textFieldSx}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Select
                value={pair.localeId}
                onChange={(e) => onLangChange(index, e.target.value)}
                variant="standard"
                disableUnderline
                disabled={disabled}
                sx={{ minWidth: 80 }}
                MenuProps={dropdownMenuProps}
              >
                {availableLocales.map((o) => (
                  <MenuItem key={o} value={o}>{Locale[o]}</MenuItem>
                ))}
              </Select>
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {!disabled && <UrlStatusIcon status={urlStatus} reason={urlReason} />}
              {previewable && !disabled && (
                <IconButton
                  size="small"
                  onClick={(e) => { e.currentTarget.blur(); onPreview(index); }}
                  disabled={!pair[attribute]}
                  title="Preview URL"
                  sx={{ color: "text.secondary", "&:hover": { color: "primary.main" } }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
      />
      {!disabled && (
        <IconButton
          onClick={() => onRemove(index)}
          color="error"
          sx={{ ml: 1 }}
          disabled={!canRemove}
        >
          <DeleteIcon />
        </IconButton>
      )}
    </Box>
  );
};

function DropdownTextField({
  label,
  values,
  onChange,
  attribute,
  previewable = false,
  mediaType = "image",
  multiline = false,
  rows = 3,
  urlValidationType = null,
  onValidationChange = null,
  disabled = false,
  required = false,
  compact = false,         // tighter layout for dense editors: no floating label, small delete icon, a text "Add language" button instead of the full-width bar
  ariaLabel = undefined,   // optional accessible name of the text inputs (multiline and plain single-line rows)
  errorText = '',          // optional validation message shown under the first row's input, for rules the caller owns
  maxLength = undefined,   // optional limit (e.g. 255 for the URL columns): a longer value shows an error, the caller blocks saving; the text is never cut
}) {
  const { availableLocales } = useLocalization();
  const [previewState, setPreviewState] = useState({ open: false, url: "" });
  const [rowStatuses, setRowStatuses] = useState({});

  const safeValues =
    values && values.length > 0
      ? values
      : [{ localeId: "en_us", [attribute]: "" }];

  // At least one locale row needs a value — matches hasRequiredName() in validators.js.
  const anyFilled = safeValues.some((v) => String(v[attribute] ?? "").trim().length > 0);
  const showRequiredHint = required && !disabled && !anyFilled;

  // Pure updater: removes stale entries when rows are deleted.
  useEffect(() => {
    if (!urlValidationType) return;
    setRowStatuses((prev) => {
      const maxIndex = safeValues.length - 1;
      const staleKeys = Object.keys(prev).filter((k) => Number(k) > maxIndex);
      if (staleKeys.length === 0) return prev;
      const next = { ...prev };
      staleKeys.forEach((k) => delete next[k]);
      return next;
    });
  }, [safeValues.length, urlValidationType]);

  // Pure updater — no side effects inside setState.
  // Stable (deps []) because it doesn't need onValidationChange in the updater.
  // Returns the same object when the row's status is unchanged, so a repeat report costs no re-render.
  const handleStatusChange = React.useCallback((index, status) => {
    setRowStatuses((prev) => (prev[index] === status ? prev : { ...prev, [index]: status }));
  }, []);

  // Single place that notifies the parent about validity.
  // Lives outside the updaters so state updaters stay pure (React Strict Mode safe).
  useEffect(() => {
    if (!onValidationChange || !urlValidationType) return;
    const statuses = Object.values(rowStatuses);
    // Only 'idle' (empty) and 'valid' (confirmed reachable) pass — 'checking' now
    // blocks too, so Save can't fire while a reachability check is still in flight.
    const allValid = statuses.every((s) => s === 'idle' || s === 'valid');
    onValidationChange(allValid);
  }, [rowStatuses, onValidationChange, urlValidationType]);

  // For row at index i: show only its own locale + locales not used by any other row.
  const getLocalesForRow = (index) => {
    const current = safeValues[index]?.localeId;
    return availableLocales.filter(
      (loc) => loc === current || !safeValues.some((v, j) => j !== index && v.localeId === loc)
    );
  };

  const usedLocaleSet = new Set(safeValues.map((v) => v.localeId));
  const nextAvailableLocale = availableLocales.find((loc) => !usedLocaleSet.has(loc));
  const canAddMore = !!nextAvailableLocale;

  const addField = () => {
    if (!canAddMore) return;
    onChange([...safeValues, { localeId: nextAvailableLocale, [attribute]: "" }]);
  };

  const handleLangChange = (index, newLang) => {
    onChange(safeValues.map((item, i) =>
      i === index ? { ...item, localeId: newLang } : item
    ));
  };

  const handleTextChange = (index, newText) => {
    onChange(safeValues.map((item, i) =>
      i === index ? { ...item, [attribute]: newText } : item
    ));
  };

  const removeField = (index) => {
    if (safeValues.length === 1) return;
    onChange(safeValues.filter((_, i) => i !== index));
  };

  const handlePreview = (index) => {
    const url = safeValues[index]?.[attribute] || "";
    setPreviewState({ open: true, url });
  };

  return (
    <Box sx={{ mb: compact ? 0 : 2, display: "flex", flexDirection: "column", justifyContent: "flex-start", mt: compact ? 0 : 1 }}>
      {safeValues.map((pair, index) =>
        multiline ? (
          // Multiline layout: locale dropdown + delete in header row, textarea below
          <Box key={index} mb={1} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ px: 1, borderBottom: "1px solid", borderColor: "divider", bgcolor: "grey.50" }}>
              <Select
                value={pair.localeId}
                onChange={(e) => handleLangChange(index, e.target.value)}
                variant="standard"
                disableUnderline
                disabled={disabled}
                sx={{ minWidth: 110, fontSize: "0.875rem" }}
                MenuProps={dropdownMenuProps}
              >
                {getLocalesForRow(index).map((o) => (
                  <MenuItem key={o} value={o}>{Locale[o]}</MenuItem>
                ))}
              </Select>
              {!disabled && (
                <IconButton
                  onClick={() => removeField(index)}
                  color={compact ? "default" : "error"}
                  size="small"
                  aria-label={compact ? "Remove language" : undefined}
                  title={compact ? "Remove this language" : undefined}
                  disabled={safeValues.length === 1}
                >
                  {compact ? <CloseIcon sx={{ fontSize: 16 }} /> : <DeleteIcon fontSize="small" />}
                </IconButton>
              )}
            </Box>
            <TextField
              fullWidth
              placeholder={label}
              value={pair[attribute]}
              onChange={(e) => handleTextChange(index, e.target.value)}
              disabled={disabled}
              multiline
              rows={rows}
              inputProps={ariaLabel ? { 'aria-label': ariaLabel } : undefined}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-notchedOutline": { border: "none" } }}
            />
            {errorText && !disabled && index === 0 && (
              <FormHelperText error sx={{ mx: 1.75, mb: 1 }}>{errorText}</FormHelperText>
            )}
          </Box>
        ) : urlValidationType ? (
          // Single-line layout with live URL validation per row
          <UrlTextFieldRow
            key={index}
            pair={pair}
            index={index}
            attribute={attribute}
            label={label}
            urlValidationType={urlValidationType}
            previewable={previewable}
            onTextChange={handleTextChange}
            onLangChange={handleLangChange}
            onRemove={removeField}
            onPreview={handlePreview}
            availableLocales={getLocalesForRow(index)}
            canRemove={safeValues.length > 1}
            onStatusChange={handleStatusChange}
            disabled={disabled}
            required={required}
            groupHasValue={anyFilled}
            maxLength={maxLength}
          />
        ) : (
          // Single-line layout without validation
          <Box key={index} display="flex" alignItems={compact ? "flex-start" : "center"} mb={1}>
            <TextField
              fullWidth
              placeholder={label}
              label={compact ? undefined : label}
              value={pair[attribute]}
              onChange={(e) => handleTextChange(index, e.target.value)}
              disabled={disabled}
              inputProps={ariaLabel ? { 'aria-label': ariaLabel } : undefined}
              error={(Boolean(maxLength) && String(pair[attribute] ?? '').trim().length > maxLength) || Boolean(errorText && !disabled && index === 0)}
              helperText={(Boolean(maxLength) && String(pair[attribute] ?? '').trim().length > maxLength) ? `At most ${maxLength} characters` : ((errorText && !disabled && index === 0) ? errorText : (showRequiredHint ? "Required" : ""))}
              sx={textFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Select
                      value={pair.localeId}
                      onChange={(e) => handleLangChange(index, e.target.value)}
                      variant="standard"
                      disableUnderline
                      disabled={disabled}
                      sx={{ minWidth: 80 }}
                      MenuProps={dropdownMenuProps}
                    >
                      {getLocalesForRow(index).map((o) => (
                        <MenuItem key={o} value={o}>{Locale[o]}</MenuItem>
                      ))}
                    </Select>
                  </InputAdornment>
                ),
                endAdornment: (previewable && !disabled) ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.currentTarget.blur(); handlePreview(index); }}
                      disabled={!pair[attribute]}
                      title="Preview URL"
                      sx={{ color: "text.secondary", "&:hover": { color: "primary.main" } }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
            {!disabled && (
              <IconButton
                onClick={() => removeField(index)}
                color={compact ? "default" : "error"}
                size={compact ? "small" : "medium"}
                aria-label={compact ? "Remove language" : undefined}
                title={compact ? "Remove this language" : undefined}
                sx={{ ml: 1, mt: compact ? 1.25 : 0 }}
                disabled={safeValues.length === 1}
              >
                {compact ? <CloseIcon sx={{ fontSize: 16 }} /> : <DeleteIcon />}
              </IconButton>
            )}
          </Box>
        )
      )}

      {!disabled && compact && (
        <Button
          size="small"
          onClick={addField}
          disabled={!canAddMore}
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          sx={{ alignSelf: "flex-start", textTransform: "none", fontSize: 12, fontWeight: 600, minHeight: 0, py: 0.25 }}
        >
          Add language
        </Button>
      )}

      {!disabled && !compact && (
      <IconButton
        onClick={addField}
        disabled={!canAddMore}
        aria-label="Add language"
        sx={{
          backgroundColor: canAddMore ? "primary.main" : "grey.300",
          "&:hover": { backgroundColor: canAddMore ? "primary.dark" : "grey.300" },
          borderRadius: 2,
          p: 1,
        }}
      >
        <ToolIcon name={"add"} />
      </IconButton>
      )}

      {previewable && (
        <MediaPreviewOverlay
          open={previewState.open}
          onClose={() => setPreviewState({ open: false, url: "" })}
          type={mediaType}
          url={previewState.url}
        />
      )}
    </Box>
  );
}

export default DropdownTextField;
